import { useState, useEffect } from "react";
import type { VehicleType, VehicleSubtype } from "@/types/subscription";

interface UseVehicleSelectionProps {
  availableVehicleTypes: VehicleType[];
}

export const useVehicleSelection = ({
  availableVehicleTypes,
}: UseVehicleSelectionProps) => {
  const [selectedVehicleType, setSelectedVehicleType] = useState<VehicleType | null>(null);
  const [selectedSubtype, setSelectedSubtype] = useState<VehicleSubtype | null>(null);
  const [vehicleTypeId, setVehicleTypeId] = useState<string>("");
  const [vehicleSubtypeId, setVehicleSubtypeId] = useState<string>("");

  // Update selected vehicle type when vehicleTypeId changes
  useEffect(() => {
    if (vehicleTypeId) {
      const selected = availableVehicleTypes.find(type => type.vt_id === vehicleTypeId);
      setSelectedVehicleType(selected || null);
    } else {
      setSelectedVehicleType(null);
    }
  }, [vehicleTypeId, availableVehicleTypes]);

  // Update selected subtype when vehicleSubtypeId changes
  useEffect(() => {
    if (vehicleSubtypeId && vehicleTypeId) {
      const selected = availableVehicleTypes
        .find(type => type.vt_id === vehicleTypeId)
        ?.subtypes.find(subtype => subtype.vst_id === vehicleSubtypeId);
      setSelectedSubtype(selected || null);
    } else {
      setSelectedSubtype(null);
    }
  }, [vehicleSubtypeId, vehicleTypeId, availableVehicleTypes]);

  // Clear subtype when vehicle type changes
  useEffect(() => {
    setVehicleSubtypeId("");
    setSelectedSubtype(null);
  }, [vehicleTypeId]);

  const selectVehicleType = (typeId: string) => {
    setVehicleTypeId(typeId);
  };

  const selectVehicleSubtype = (subtypeId: string) => {
    setVehicleSubtypeId(subtypeId);
  };

  const resetSelection = () => {
    setVehicleTypeId("");
    setVehicleSubtypeId("");
    setSelectedVehicleType(null);
    setSelectedSubtype(null);
  };

  return {
    selectedVehicleType,
    selectedSubtype,
    vehicleTypeId,
    vehicleSubtypeId,
    selectVehicleType,
    selectVehicleSubtype,
    resetSelection
  };
}; 